import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Clock, Send, ChevronDown } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { API } from "../lib/api";

const services = ["Electrical", "Plumbing", "Furniture", "Turnkey (All Three)", "Other"];

const inputCls =
  "w-full border border-[#2E333D] bg-[#0B0C0E] px-4 py-3.5 text-sm text-[#F3F4F6] placeholder-[#6B7280] outline-none transition-colors focus:border-[#FFFFFF]";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "Electrical", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, email: form.email || null });
      toast.success("Enquiry received. Our team will call you back shortly.");
      setForm({ name: "", phone: "", email: "", service: "Electrical", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your enquiry. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="scroll-mt-24 bg-[#14171D] py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <div>
          <Reveal>
            <Eyebrow>Direct contact</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tight text-[#F3F4F6] sm:text-5xl">
              Let's build
              <br />
              <span className="text-[#FFFFFF]">your project.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#9CA3AF]">
              Tell us what you're building — Vinod and the team respond personally to every enquiry.
            </p>
          </Reveal>

          <div className="mt-10 space-y-4">
            <Reveal delay={0.1}>
              <a
                href="tel:+917709322253"
                data-testid="contact-phone-card"
                className="group flex items-center gap-5 border border-[#2E333D] bg-[#1A1D24] p-6 transition-colors hover:border-[#FFFFFF]/60"
              >
                <span className="flex h-12 w-12 items-center justify-center bg-[#FFFFFF] text-black">
                  <Phone size={20} />
                </span>
                <span>
                  <span className="block font-mono text-[11px] uppercase tracking-[0.25em] text-[#9CA3AF]">Call us directly</span>
                  <span className="block font-display text-2xl font-bold tracking-tight text-[#F3F4F6] group-hover:text-[#FFFFFF]">
                    77093 22253
                  </span>
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.15}>
              <a
                href="mailto:gativan55@gmail.com"
                data-testid="contact-email-card"
                className="group flex items-center gap-5 border border-[#2E333D] bg-[#1A1D24] p-6 transition-colors hover:border-[#FFFFFF]/60"
              >
                <span className="flex h-12 w-12 items-center justify-center bg-[#FFFFFF] text-black">
                  <Mail size={20} />
                </span>
                <span>
                  <span className="block font-mono text-[11px] uppercase tracking-[0.25em] text-[#9CA3AF]">Email us</span>
                  <span className="block font-display text-2xl font-bold tracking-tight text-[#F3F4F6] group-hover:text-[#FFFFFF]">
                    gativan55@gmail.com
                  </span>
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="flex items-center gap-5 border border-[#2E333D] bg-[#1A1D24] p-6">
                <span className="flex h-12 w-12 items-center justify-center border border-[#FFFFFF]/50 text-[#FFFFFF]">
                  <Clock size={20} />
                </span>
                <span>
                  <span className="block font-mono text-[11px] uppercase tracking-[0.25em] text-[#9CA3AF]">Working hours</span>
                  <span className="block font-display text-2xl font-bold tracking-tight text-[#F3F4F6]">Mon – Sat · 9:00 – 19:00</span>
                </span>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="border border-[#2E333D] bg-[#1A1D24] p-8 sm:p-10"
          >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFFFFF]">Project enquiry</p>
            <div className="mt-6 grid gap-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <input
                  data-testid="contact-input-name"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name *"
                  className={inputCls}
                />
                <input
                  data-testid="contact-input-phone"
                  required
                  minLength={7}
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Phone number *"
                  className={inputCls}
                />
              </div>
              <input
                data-testid="contact-input-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Email address (optional)"
                className={inputCls}
              />
              <div className="relative">
                <select
                  data-testid="contact-select-service"
                  value={form.service}
                  onChange={set("service")}
                  className={`${inputCls} appearance-none pr-12 cursor-pointer`}
                >
                  {services.map((s) => (
                    <option key={s} value={s} className="bg-[#0B0C0E]">
                      {s}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={18}
                  className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
                  aria-hidden="true"
                />
              </div>
              <textarea
                data-testid="contact-textarea-message"
                required
                minLength={5}
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us about your project — location, scope, timeline *"
                className={`${inputCls} resize-none`}
              />
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading}
                className="group flex items-center justify-center gap-3 bg-[#FFFFFF] px-8 py-4 font-mono text-sm font-semibold uppercase tracking-[0.15em] text-black transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send Enquiry"}
                <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
